import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Globe, Facebook, Instagram, Music2, Handshake, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { EmptyState } from '../components/commerce/EmptyState';
import { fileUrl } from '../lib/upload';

const SOCIALS = [
  { key: 'website', label: 'Website', icon: Globe },
  { key: 'facebook', label: 'Facebook', icon: Facebook },
  { key: 'instagram', label: 'Instagram', icon: Instagram },
  { key: 'tiktok', label: 'TikTok', icon: Music2 },
];

export default function PartnersPage() {
  const { partners } = useApp();

  return (
    <div data-testid="partners-page" className="mx-auto max-w-[1280px] px-4 py-10">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-kelate-wood">Collaboration Partners</p>
      <h1 className="mt-2 font-display text-3xl font-semibold text-kelate-darkwood sm:text-4xl">Growing Kelantan, together.</h1>
      <p className="mt-1 max-w-lg text-sm text-kelate-soft">
        KELATE works with organisations that champion local businesses. Tap a partner to visit their page.
      </p>

      {partners.length === 0 ? (
        <div className="mt-8"><EmptyState icon={Handshake} title="Partners coming soon" description="KELATE is announcing collaboration partners shortly." /></div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="partners-grid">
          {partners.map((p) => (
            <Link
              key={p.id}
              to={`/partners/${p.id}`}
              data-testid={`partner-logo-${p.id}`}
              className="group flex flex-col items-center gap-3 rounded-3xl bg-kelate-surface p-6 text-center shadow-[0_1px_4px_rgba(81,68,55,0.08)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(81,68,55,0.14)]"
            >
              {p.profilePhoto || p.logo ? (
                <img src={fileUrl(p.profilePhoto || p.logo)} alt={p.name} className="h-16 w-16 rounded-2xl object-cover" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-kelate-darkwood font-display text-2xl font-semibold text-kelate-surface">
                  {p.name[0]}
                </span>
              )}
              <span className="text-sm font-semibold text-kelate-ink group-hover:text-kelate-darkwood">{p.name}</span>
              <span className="text-[11px] text-kelate-soft">View partner page</span>
            </Link>
          ))}
        </div>
      )}

    </div>
  );
}

export function PartnerDetailPage() {
  const { id } = useParams();
  const { partners } = useApp();
  const p = partners.find((x) => x.id === id);

  if (!p) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16">
        <EmptyState icon={Handshake} title="Partner not found" description="This partner page does not exist or was removed." actionLabel="All Partners" actionTo="/partners" testId="partner-not-found" />
      </div>
    );
  }

  return (
    <div data-testid="partner-detail-page" className="mx-auto max-w-[760px] px-4 py-10">
      <Link to="/partners" data-testid="back-to-partners" className="inline-flex items-center gap-1.5 text-sm text-kelate-soft hover:text-kelate-wood">
        <ArrowLeft size={14} /> All partners
      </Link>
      <div className="mt-4 overflow-hidden rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <div className="relative bg-kelate-darkwood px-8 pb-10 pt-16 text-center">
          {p.coverPhoto && <img src={fileUrl(p.coverPhoto)} alt="" data-testid="partner-detail-cover" className="absolute inset-0 h-full w-full object-cover opacity-50" />}
          <div className="relative mx-auto w-fit">
            {p.profilePhoto || p.logo ? (
              <img src={fileUrl(p.profilePhoto || p.logo)} alt={p.name} className="aspect-square h-24 w-24 rounded-3xl border-2 border-kelate-surface/60 object-cover" data-testid="partner-detail-profile" />
            ) : (
              <span className="flex h-24 w-24 items-center justify-center rounded-3xl bg-kelate-surface font-display text-4xl font-semibold text-kelate-darkwood" data-testid="partner-detail-profile">{p.name[0]}</span>
            )}
          </div>
          <h1 className="relative mt-4 font-display text-3xl font-semibold text-kelate-surface" data-testid="partner-detail-name">{p.name}</h1>
          <p className="relative mt-1 text-xs uppercase tracking-[0.2em] text-kelate-wood">KELATE Collaboration Partner</p>
        </div>
        <div className="p-8">
          {p.description && <p className="text-center text-sm leading-relaxed text-kelate-soft" data-testid="partner-detail-description">{p.description}</p>}
          <h2 className="mt-8 text-xs font-semibold uppercase tracking-widest text-kelate-soft">Official channels</h2>
          <div className="mt-3 space-y-2">
            {SOCIALS.filter((s) => p.socials?.[s.key]).map((s) => (
              <a
                key={s.key}
                data-testid={`partner-social-${s.key}`}
                href={p.socials[s.key]}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-2xl bg-secondary/60 px-4 py-3.5 text-sm font-medium text-kelate-ink transition-all hover:-translate-y-0.5 hover:bg-secondary hover:shadow-[0_6px_16px_rgba(81,68,55,0.1)]"
              >
                <s.icon size={17} className="text-kelate-wood" />
                {s.label}
                <span className="ml-auto truncate text-xs text-kelate-soft">{p.socials[s.key].replace(/^https?:\/\//, '')}</span>
              </a>
            ))}
            {!SOCIALS.some((s) => p.socials?.[s.key]) && (
              <p className="py-4 text-center text-sm text-kelate-soft">No public channels listed yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
